import { FilterDataProps, StoreListItem } from "@/types/modules/filters";

import { Icon } from "@cobasi/ds-icons";
import Services from "@cobasi/ds-icons/solid/services";
import HouseChimneyMedical from "@cobasi/ds-icons/solid/houseChimmeyMedical";
import PersonalPet from "@cobasi/ds-icons/solid/vestPatches";
import TennisBall from "@cobasi/ds-icons/solid/ball";
import CircleParking from "@cobasi/ds-icons/solid/circleParking";
import Capsules from "@cobasi/ds-icons/solid/capsules";
import Store from "@cobasi/ds-icons/solid/store";

export const filterDataInitial: FilterDataProps = {
  state: "",
  city: "",
  neighborhood: "",
  facilidades: [],
  zipcode: "",
  filterBy: "address"
};

export const PET_ANJO_FILTER_CHIPS = ["Banho e Tosa", "Clínica Veterinária"];

export const sliderSettings = {
  className: "slider variable-width",
  dots: false,
  infinite: false,
  variableWidth: true,
  swipeToSlide: true,
  slidesToScroll: 3,
  slidesToShow: 6,
  responsive: [
    {
      breakpoint: 1215,
      settings: {
        slidesToShow: 5
      }
    },
    {
      breakpoint: 960,
      settings: {
        slidesToShow: 4
      }
    },
    {
      breakpoint: 830,
      settings: {
        slidesToShow: 3
      }
    },
    {
      breakpoint: 500,
      settings: {
        slidesToShow: 2
      }
    }
  ]
};

export const chips = [
  {
    icon: <Icon icon={Services} width="1.8rem" height="1.8rem" />,
    title: "Banho e Tosa",
    tags: ["Banho e Tosa", "Banho & Tosa"]
  },
  {
    icon: <Icon icon={HouseChimneyMedical} width="1.8rem" height="1.8rem" />,
    title: "Clínica Veterinária",
    tags: ["Clínica Veterinária", "Clínica veterinária", "Veterinário"]
  },
  {
    icon: <Icon icon={PersonalPet} width="1.8rem" height="1.8rem" />,
    title: "Personal Pet",
    tags: ["Personal Pet"]
  },
  {
    icon: <Icon icon={TennisBall} width="1.8rem" height="1.8rem" />,
    title: "Pet Park",
    tags: ["Pet Park"]
  },
  {
    icon: <Icon icon={CircleParking} width="1.8rem" height="1.8rem" />,
    title: "Estacionamento",
    tags: [
      "Estacionamento",
      "Estacionamento coberto/gratuito",
      "Estacionamento coberto/pago",
      "Estacionamento descoberto/gratuito",
      "Estacionamento descoberto/pago"
    ]
  },
  {
    icon: <Icon icon={Capsules} width="1.8rem" height="1.8rem" />,
    title: "Farmácia Veterinária",
    tags: ["Farmácia Veterinária"]
  },
  {
    icon: <Icon icon={Store} width="1.8rem" height="1.8rem" />,
    title: "Retire na loja",
    tags: ["Retire na loja", "Retire na loja em até 45 minutos"]
  }
];

// Retorna as opções de estados em ordem alfabética
export const getStatesOptions = (
  storeListData: StoreListItem[]
): { value: string; label: string }[] => {
  if (!storeListData || storeListData.length === 0) {
    return [];
  }

  const states = storeListData.map(({ address }) => address.state.trim());
  const uniqueStates = Array.from(new Set(states));
  const sortedStates = uniqueStates.sort((a, b) => a.localeCompare(b));
  const stateOptions = sortedStates.map((state) => ({
    value: state,
    label: state
  }));
  return stateOptions;
};

// Retorna as opções de cidades de acordo com o estado selecionado em ordem alfabética
export const getCitiesOptions = (
  storeListData: StoreListItem[],
  state: string
): { value: string; label: string }[] => {
  if (!storeListData || storeListData.length === 0) {
    return [];
  }

  const cities = storeListData
    .filter(({ address }) => address.state === state)
    .map(({ address }) => address.city.trim());
  const uniqueCities = Array.from(new Set(cities));
  const sortedCities = uniqueCities.sort((a, b) => a.localeCompare(b));
  const cityOptions = sortedCities.map((city) => ({
    value: city,
    label: city
  }));
  return cityOptions;
};

// Retorna as opções de bairros de acordo com o estado e cidade selecionados em ordem alfabética
export const getNeighborhoodsOptions = (
  storeListData: StoreListItem[],
  state: string,
  city: string
): { value: string; label: string }[] => {
  if (!storeListData || storeListData.length === 0) {
    return [];
  }

  const neighborhoods = storeListData
    .filter(({ address }) => address.state === state && address.city === city)
    .map(({ address }) => address.neighborhood.trim());

  const uniqueNeighborhoods = Array.from(new Set(neighborhoods));

  const sortedNeighborhoods = uniqueNeighborhoods.sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" })
  );

  const neighborhoodOptions = sortedNeighborhoods.map((neighborhood) => ({
    value: neighborhood,
    label: neighborhood
  }));

  return neighborhoodOptions;
};

const toChipSlug = (title: string) =>
  title
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const hasQueryFlag = (
  key: string,
  searchParams?: URLSearchParams | null,
  initialSearchParams?: { [key: string]: string | string[] | undefined }
) => {
  if (
    initialSearchParams &&
    Object.prototype.hasOwnProperty.call(initialSearchParams, key)
  ) {
    return true;
  }

  return searchParams?.has(key) ?? false;
};

const getQueryValues = (
  key: string,
  searchParams?: URLSearchParams | null,
  initialSearchParams?: { [key: string]: string | string[] | undefined }
): string[] => {
  const fromInitial = initialSearchParams?.[key];

  if (fromInitial !== undefined) {
    return (Array.isArray(fromInitial) ? fromInitial : [fromInitial])
      .flatMap((value) => String(value).split(","))
      .map((value) => value.trim())
      .filter(Boolean);
  }

  if (!searchParams) return [];

  return searchParams
    .getAll(key)
    .flatMap((value) => value.split(","))
    .map((value) => value.trim())
    .filter(Boolean);
};

export const storeMatchesFacilities = (
  store: StoreListItem,
  facilidades: string[]
) => {
  if (facilidades.length === 0) return true;

  const matchesFacility = (filterFacility: string) => {
    const chip = chips.find((item) => item.title === filterFacility);

    return store.facilidades.some((storeFacility) =>
      Boolean(chip?.tags.includes(storeFacility.name))
    );
  };

  const onlyPetAnjoFacilities = facilidades.every((facility) =>
    PET_ANJO_FILTER_CHIPS.includes(facility)
  );

  return onlyPetAnjoFacilities
    ? facilidades.some(matchesFacility)
    : facilidades.every(matchesFacility);
};

export const getInitialFilterData = (
  searchParams?: URLSearchParams | null,
  initialSearchParams?: { [key: string]: string | string[] | undefined }
): FilterDataProps => {
  const petanjoParam = getQueryValues(
    "petanjo",
    searchParams,
    initialSearchParams
  )[0];
  const isPetAnjoRedirect = petanjoParam === "true" || petanjoParam === "1";

  if (isPetAnjoRedirect) {
    return {
      ...filterDataInitial,
      facilidades: [...PET_ANJO_FILTER_CHIPS]
    };
  }

  const chipTitles = chips.map((chip) => chip.title);
  const facilidadesFromQuery = getQueryValues(
    "facilidades",
    searchParams,
    initialSearchParams
  )
    .map((value) => {
      try {
        return decodeURIComponent(value);
      } catch {
        return value;
      }
    })
    .filter((value) => chipTitles.includes(value));

  const facilidadesFromSlug = chips
    .filter((chip) =>
      hasQueryFlag(toChipSlug(chip.title), searchParams, initialSearchParams)
    )
    .map((chip) => chip.title);

  return {
    ...filterDataInitial,
    facilidades: Array.from(
      new Set([...facilidadesFromQuery, ...facilidadesFromSlug])
    )
  };
};
